/**
 * "Move to live now" — the row action, in the two shapes the Queue tab uses:
 * a labelled button (Waiting rows, where it is the point) and a bare icon
 * (Queue rows, where it sits beside Move-to-waiting).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import {
  IconMoveToLive,
  queueActionGlyph,
  queueActionSurface,
} from '../queue-action-style';
import type { QueueElement } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import { usePromoteFlow } from './promote-flow';

export interface MoveToLiveButtonProps {
  element: QueueElement;
  /** Bare icon instead of a labelled button. */
  compact?: boolean;
  /** The row's OTHER action is mid-flight — one row, one write at a time. */
  busy?: boolean;
}

const LABEL = 'Move to live now';

const MoveToLiveButton: FC<MoveToLiveButtonProps> = ({
  element,
  compact,
  busy = false,
}) => {
  const flow = usePromoteFlow();
  if (!flow) return null;
  const pending = flow.pendingBankId === element.bankId || busy;
  const title = flow.disabledReason ?? `${LABEL} — deploys it to the tracker`;

  if (compact) {
    return (
      <button
        type="button"
        title={title}
        aria-label={`${LABEL}: ${element.itemName || element.itemId}`}
        disabled={Boolean(flow.disabledReason) || pending}
        onClick={() => flow.request(element)}
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-40',
          // Bare icon, so here the glyph itself has to carry the green.
          queueActionGlyph('live'),
          'hover:bg-success/15',
        )}
      >
        <IconMoveToLive className="h-3.5 w-3.5" aria-hidden />
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={cn('h-7 shrink-0 gap-1 text-[11px]', queueActionSurface('live'))}
      title={title}
      loading={pending}
      disabled={Boolean(flow.disabledReason) || pending}
      onClick={() => flow.request(element)}
    >
      <IconMoveToLive className={cn('h-3.5 w-3.5', queueActionGlyph('live'))} aria-hidden />
      {LABEL}
    </Button>
  );
};

export default MoveToLiveButton;
